import React from "react";

import { getStaticColumnsTotalWidth, WEEKLY_COLUMN_WIDTH } from "./weeklyEACColumns";
import {
  ODC_STATIC_COLUMNS,
  ODC_STATIC_COLUMNS_TOTAL_WIDTH,
  ODC_WEEKLY_BUCKET_COLUMN_WIDTHS,
} from "./weeklyEACODCColumns";
import { formatCellValue } from "./weeklyEACFormat";
import { getBucketForWeek } from "./weeklyEACCalculations";
import WeeklyEACODCWeeklyTotalRow from "./WeeklyEACODCWeeklyTotalRow";

export default function WeeklyEACODCWeeklyTable({
  odcDetails,
  allOdcDetails,
  weekColumns,
  type,
  isEdit,
  onUnburdenCostChange,
}) {
  // ODC's own static columns are narrower than Direct Labor's, so the remainder is padded with a
  // blank spacer column — that keeps week 0 starting at the same scrollLeft offset as Direct
  // Labor's and Summary's, which is what makes the 3-way scroll sync line the same week up
  // visually across all three tables.
  const spacerWidth = Math.max(
    getStaticColumnsTotalWidth(type) - ODC_STATIC_COLUMNS_TOTAL_WIDTH,
    0,
  );

  return (
    <table className="secondTableFin weeklyEac" cellPadding={0} cellSpacing={0}>
      <thead>
        <tr>
          {spacerWidth > 0 && (
            <th rowSpan={2} style={{ minWidth: spacerWidth, maxWidth: spacerWidth }} />
          )}
          {ODC_STATIC_COLUMNS.map((column) => (
            <th
              key={column.label}
              rowSpan={2}
              className="centerAlignedHeaderLeftBorder"
              style={{ minWidth: column.width, maxWidth: column.width }}
            >
              {column.label}
            </th>
          ))}
          {weekColumns.map((week, index) => (
            <th
              key={week.weekStartDate}
              colSpan={2}
              className="centerAlignedHeaderLeftBorder"
              style={{
                minWidth: WEEKLY_COLUMN_WIDTH,
                maxWidth: WEEKLY_COLUMN_WIDTH,
                ...(index === weekColumns.length - 1
                  ? { borderRight: "1px solid #fff" }
                  : {}),
              }}
            >
              {formatCellValue(week.weekEndDate, "date")}
            </th>
          ))}
        </tr>
        <tr>
          {weekColumns.map((week, index) => {
            const headerClass =
              week.type === "Actual" ? "actualHeaderCost" : "forecastHeaderCost";
            const isLastWeek = index === weekColumns.length - 1;
            return (
              <React.Fragment key={week.weekStartDate}>
                <th
                  className={headerClass}
                  style={{
                    minWidth: ODC_WEEKLY_BUCKET_COLUMN_WIDTHS.unburdenCost,
                    maxWidth: ODC_WEEKLY_BUCKET_COLUMN_WIDTHS.unburdenCost,
                  }}
                >
                  Unburden Cost
                </th>
                <th
                  className={headerClass}
                  style={{
                    minWidth: ODC_WEEKLY_BUCKET_COLUMN_WIDTHS.burdenCost,
                    maxWidth: ODC_WEEKLY_BUCKET_COLUMN_WIDTHS.burdenCost,
                    ...(isLastWeek ? { borderRight: "1px solid #ccc" } : {}),
                  }}
                >
                  Burden Cost
                </th>
              </React.Fragment>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {odcDetails.map((odc) => (
          <tr key={odc.odcId}>
            {spacerWidth > 0 && (
              <td style={{ minWidth: spacerWidth, maxWidth: spacerWidth }} />
            )}
            {ODC_STATIC_COLUMNS.map((column) => (
              <td
                key={column.label}
                style={{ minWidth: column.width, maxWidth: column.width }}
                title={column.accessor(odc) ?? ""}
              >
                {column.accessor(odc)}
              </td>
            ))}
            {weekColumns.map((week, index) => {
              const bucket = getBucketForWeek(odc, week.weekStartDate);
              const editableUnburdenCost = isEdit && bucket?.type === "Forecast";
              const isLastWeek = index === weekColumns.length - 1;
              return (
                <React.Fragment key={week.weekStartDate}>
                  <td
                    style={{
                      minWidth: ODC_WEEKLY_BUCKET_COLUMN_WIDTHS.unburdenCost,
                      maxWidth: ODC_WEEKLY_BUCKET_COLUMN_WIDTHS.unburdenCost,
                      borderLeft: "1px solid #ccc",
                      paddingLeft: "4px",
                    }}
                    title={formatCellValue(bucket?.unburdenCost, "currency")}
                  >
                    {editableUnburdenCost ? (
                      <input
                        type="number"
                        value={bucket?.unburdenCost ?? ""}
                        style={{ width: "70px" }}
                        onChange={(e) =>
                          onUnburdenCostChange(odc.odcId, week.weekStartDate, e.target.value)
                        }
                      />
                    ) : (
                      formatCellValue(bucket?.unburdenCost, "currency")
                    )}
                  </td>
                  <td
                    style={{
                      minWidth: ODC_WEEKLY_BUCKET_COLUMN_WIDTHS.burdenCost,
                      maxWidth: ODC_WEEKLY_BUCKET_COLUMN_WIDTHS.burdenCost,
                      ...(isLastWeek ? { borderRight: "1px solid #ccc" } : {}),
                    }}
                    title={formatCellValue(bucket?.burdenCost, "currency")}
                  >
                    {formatCellValue(bucket?.burdenCost, "currency")}
                  </td>
                </React.Fragment>
              );
            })}
          </tr>
        ))}
        <WeeklyEACODCWeeklyTotalRow
          odcDetails={allOdcDetails}
          weekColumns={weekColumns}
          staticColumns={ODC_STATIC_COLUMNS}
          spacerWidth={spacerWidth}
        />
      </tbody>
    </table>
  );
}
